/* eslint-disable @typescript-eslint/no-explicit-any -- There is a lot of old calls in coffeescript or js files not typed */
import Consts from 'consts'
import { refreshTokenIfNeeded } from 'web-client/utils/auth-refresh'
// eslint-disable-next-line no-restricted-imports -- use datadog and rollbar directly
import logging from 'shared/utils/logging'
import type { FetchResponse } from 'web-client/utils/swoopFetch'
import swoopFetch from 'web-client/utils/swoopFetch'
import SwoopHeaders from 'web-client/utils/swoopHeaders'
import type { Context } from '@datadog/browser-core'
import relayService from 'shared/utils/relay'

const GET = 'GET'
const POST = 'POST'
const DELETE = 'DELETE'
const UPDATE = 'PATCH'

const Api = {
  GET,
  POST,
  DELETE,
  UPDATE,

  setBearerToken(token: string) {
    SwoopHeaders.setBearerToken(token)
    SwoopHeaders.syncAuthorizationCompanyHeader()
  },

  async request(
    method: string,
    url: string,
    data: object | null | undefined,
    callbacks: {
      success?: (data?: any) => void
      error?: (response: any, jQueryTextStatus: '', responseText: string | undefined) => void
    } = {},
    options: {
      ajaxOptions?: { dataType?: 'json' | 'text' }
      ignore401?: boolean
      ignoreFrontendFailure?: boolean
      overrideUrl?: boolean
    } = {}
  ): Promise<void | FetchResponse> {
    const { error: errorCallback, success: successCallback } = callbacks ?? {}

    const localOptions = options
    let apiUrl = `${Consts.API_PATH}${url}`

    if (localOptions.overrideUrl) {
      apiUrl = url
    }

    const token = await refreshTokenIfNeeded(relayService.environment)

    Api.setBearerToken(token as string)

    let response: FetchResponse | undefined

    try {
      response = await swoopFetch(apiUrl, {
        headers: SwoopHeaders.headers,
        method,
        ...(data &&
          Object.keys(data).length && {
            body: JSON.stringify(data),
          }),
      })
    } catch (err) {
      const responseText = 'Something went wrong with the network'
      logging.logInfo('[api.ts] network error', err as Context)
      return errorCallback?.(
        {
          status: response?.status,
          responseText,
        },
        '',
        responseText
      )
    }

    if (response.ok) {
      if (successCallback) {
        const responseText = await response.text()

        successCallback(
          options?.ajaxOptions?.dataType === 'text' || !responseText
            ? responseText
            : JSON.parse(responseText)
        )
      }
    } else if (response.status === 401 && !localOptions.ignore401) {
      try {
        const tkn = await refreshTokenIfNeeded(relayService.environment, true)

        Api.setBearerToken(tkn as string)
      } catch (e) {
        const responseText = await response.text()

        return errorCallback?.(
          {
            ...(options?.ajaxOptions?.dataType !== 'text' && {
              responseJSON: JSON.parse(responseText as string),
            }),
            status: response.status,
            responseText,
          },
          '',
          responseText
        )
      }
      return Api.request(
        method,
        url,
        data,
        { error: errorCallback, success: successCallback },
        { ...localOptions, ignore401: true }
      )
    } else if (response.status === 0 && !localOptions.ignoreFrontendFailure) {
      return Api.request(
        method,
        url,
        data,
        { error: errorCallback, success: successCallback },
        { ...localOptions, ignoreFrontendFailure: true }
      )
    } else {
      const responseText = await response.text()

      const errorResponse = { status: response.status, responseText }

      if (options?.ajaxOptions?.dataType !== 'text') {
        try {
          // @ts-expect-error
          errorResponse.responseJSON = JSON.parse(responseText)
        } catch (e) {
          // we could not parse JSON, so it is probably not JSON
        }
      }

      return errorCallback?.(errorResponse, '', responseText)
    }

    return response
  },
}

export default Api
